import {
  Alert,
  App,
  Button,
  Drawer,
  Empty,
  Form,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  academicsApi,
  type ClassSubject,
  type ClassSubjectInput,
  type Medium,
} from '@/api/academics';
import { setupApi } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

interface FormShape {
  subjectId: number;
  compulsory: boolean;
  medium: Medium;
  hasPractical: boolean;
  theoryTotalMarks: number;
  theoryPassingMarks: number;
  practicalTotalMarks?: number;
  practicalPassingMarks?: number;
  mustPassBoth: boolean;
}

export function ClassSubjectsPage() {
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('ACADEMICS:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('ACADEMICS:EDIT'));
  const canDelete = useAuthStore((s) => s.hasAuthority('ACADEMICS:DELETE'));
  const academicYearId = useAuthStore((s) => s.academicYearId);

  const [classSectionId, setClassSectionId] = useState<number | undefined>(undefined);
  const [editing, setEditing] = useState<ClassSubject | null>(null);
  const [creating, setCreating] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [form] = Form.useForm<FormShape>();

  const { data: sections } = useQuery({
    queryKey: ['setup', 'class-sections', academicYearId],
    queryFn: () => setupApi.listClassSections(academicYearId ?? undefined),
    staleTime: 60_000,
  });

  const { data: subjects } = useQuery({
    queryKey: ['setup', 'lookups', 'subjects', 'active'],
    queryFn: () => setupApi.listLookup('subjects', true),
    staleTime: 5 * 60_000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['academics', 'class-subjects', classSectionId],
    queryFn: () => academicsApi.listClassSubjects(classSectionId!),
    enabled: classSectionId != null,
  });

  const upsert = useMutation({
    mutationFn: (v: FormShape) => {
      const body: ClassSubjectInput = {
        subjectId: v.subjectId,
        compulsory: v.compulsory,
        medium: v.medium,
        hasPractical: v.hasPractical,
        theoryTotalMarks: v.theoryTotalMarks,
        theoryPassingMarks: v.theoryPassingMarks,
        practicalTotalMarks: v.hasPractical ? v.practicalTotalMarks : undefined,
        practicalPassingMarks: v.hasPractical ? v.practicalPassingMarks : undefined,
        mustPassBoth: v.hasPractical && v.mustPassBoth,
      };
      return editing
        ? academicsApi.updateClassSubject(editing.id, body)
        : academicsApi.createClassSubject(classSectionId!, body);
    },
    onSuccess: () => {
      message.success(editing ? 'Subject updated' : 'Subject assigned');
      qc.invalidateQueries({ queryKey: ['academics', 'class-subjects', classSectionId] });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => academicsApi.deleteClassSubject(id),
    onSuccess: () => {
      message.success('Subject removed from section');
      qc.invalidateQueries({ queryKey: ['academics', 'class-subjects', classSectionId] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
    form.setFieldsValue({
      compulsory: true,
      medium: 'ENGLISH',
      hasPractical: false,
      theoryTotalMarks: 100,
      theoryPassingMarks: 33,
      mustPassBoth: false,
    });
  };
  const openEdit = (row: ClassSubject) => {
    setCreating(false);
    setEditing(row);
    form.setFieldsValue({
      subjectId: row.subjectId,
      compulsory: row.compulsory,
      medium: row.medium,
      hasPractical: row.hasPractical,
      theoryTotalMarks: row.theoryTotalMarks,
      theoryPassingMarks: row.theoryPassingMarks,
      practicalTotalMarks: row.practicalTotalMarks ?? undefined,
      practicalPassingMarks: row.practicalPassingMarks ?? undefined,
      mustPassBoth: row.mustPassBoth,
    });
  };
  const open = creating || editing != null;
  const watchHasPractical = Form.useWatch('hasPractical', form);

  const sectionOptions = (sections ?? []).map((s) => ({
    value: s.id,
    label: `${s.className} · ${s.sectionName} · ${s.academicYearName}`,
  }));

  const usedSubjectIds = new Set((data ?? []).map((c) => c.subjectId));
  const subjectOptions = (subjects ?? [])
    .filter((s) => !editing || !usedSubjectIds.has(s.id) || s.id === editing.subjectId)
    .map((s) => ({ value: s.id, label: s.name }));

  const columns: TableProps<ClassSubject>['columns'] = [
    {
      title: 'Subject',
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>{row.subjectName}</Typography.Text>
          <Space size={6}>
            <Tag color={row.compulsory ? 'processing' : 'default'}>
              {row.compulsory ? 'Compulsory' : 'Elective'}
            </Tag>
            <Tag>{row.medium}</Tag>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Theory',
      width: 180,
      align: 'right',
      render: (_, row) => (
        <Typography.Text>
          {row.theoryPassingMarks} / {row.theoryTotalMarks}
        </Typography.Text>
      ),
    },
    {
      title: 'Practical',
      width: 220,
      align: 'right',
      render: (_, row) =>
        row.hasPractical ? (
          <Space orientation="vertical" size={0} style={{ textAlign: 'right' }}>
            <Typography.Text>
              {row.practicalPassingMarks} / {row.practicalTotalMarks}
            </Typography.Text>
            {row.mustPassBoth && (
              <Tag color="warning" style={{ fontSize: 11 }}>
                Must pass both
              </Tag>
            )}
          </Space>
        ) : (
          <span style={{ opacity: 0.4 }}>—</span>
        ),
    },
    {
      title: 'Actions',
      width: 160,
      align: 'right',
      render: (_, row) => (
        <Space size={4}>
          {canEdit && (
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          )}
          {canDelete && (
            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() =>
                modal.confirm({
                  title: `Remove ${row.subjectName}?`,
                  content: 'Existing marks will become orphaned. Only do this if no exams have been recorded.',
                  okType: 'danger',
                  onOk: () => remove.mutate(row.id),
                })
              }
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Academics"
        title="Class subjects"
        description="Subjects assigned to each class section, with theory / practical marks and pass criteria."
        count={classSectionId != null && data ? { value: data.length, label: 'subjects' } : undefined}
        actions={
          canCreate && classSectionId != null ? (
            <Space wrap>
              <Button icon={<CopyOutlined />} onClick={() => setCopyOpen(true)}>
                Copy from section
              </Button>
              <Button icon={<UnorderedListOutlined />} onClick={() => setBulkOpen(true)}>
                Bulk assign
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                Assign subject
              </Button>
            </Space>
          ) : null
        }
      />

      <Space wrap size={12}>
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Pick a class section"
          style={{ minWidth: 360 }}
          value={classSectionId}
          onChange={(v) => setClassSectionId(v)}
          options={sectionOptions}
        />
      </Space>

      {classSectionId == null ? (
        <Empty description="Pick a class section to manage its subjects" />
      ) : data && data.length === 0 && !isLoading ? (
        <Empty description="No subjects assigned to this section yet" />
      ) : (
        <Table
          rowKey="id"
          size="middle"
          loading={isLoading}
          columns={columns}
          dataSource={data ?? []}
          pagination={false}
        />
      )}

      <Drawer
        title={editing ? `Edit · ${editing.subjectName}` : 'Assign subject to section'}
        open={open}
        onClose={() => {
          setEditing(null);
          setCreating(false);
        }}
        size={520}
        destroyOnHidden
        extra={
          <Space>
            <Button
              onClick={() => {
                setEditing(null);
                setCreating(false);
              }}
            >
              Cancel
            </Button>
            <Button type="primary" loading={upsert.isPending} onClick={() => form.submit()}>
              {editing ? 'Save' : 'Assign'}
            </Button>
          </Space>
        }
      >
        {open && (
          <Form form={form} layout="vertical" onFinish={(v) => upsert.mutate(v)}>
            <Form.Item label="Subject" name="subjectId" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                disabled={!!editing}
                options={subjectOptions}
              />
            </Form.Item>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item label="Compulsory" name="compulsory" valuePropName="checked" style={{ flex: 1 }}>
                <Switch />
              </Form.Item>
              <Form.Item label="Medium" name="medium" rules={[{ required: true }]} style={{ flex: 1 }}>
                <Select
                  options={[
                    { value: 'ENGLISH', label: 'English' },
                    { value: 'URDU', label: 'Urdu' },
                  ]}
                />
              </Form.Item>
            </Space.Compact>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                label="Theory total"
                name="theoryTotalMarks"
                rules={[{ required: true, type: 'number', min: 1 }]}
                style={{ flex: 1 }}
              >
                <InputNumber style={{ width: '100%' }} min={1} max={1000} step={5} />
              </Form.Item>
              <Form.Item
                label="Theory passing"
                name="theoryPassingMarks"
                rules={[{ required: true, type: 'number', min: 0 }]}
                style={{ flex: 1 }}
              >
                <InputNumber style={{ width: '100%' }} min={0} max={1000} step={1} />
              </Form.Item>
            </Space.Compact>
            <Form.Item label="Has practical" name="hasPractical" valuePropName="checked">
              <Switch />
            </Form.Item>
            {watchHasPractical && (
              <>
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item
                    label="Practical total"
                    name="practicalTotalMarks"
                    rules={[{ required: true, type: 'number', min: 1 }]}
                    style={{ flex: 1 }}
                  >
                    <InputNumber style={{ width: '100%' }} min={1} max={1000} step={5} />
                  </Form.Item>
                  <Form.Item
                    label="Practical passing"
                    name="practicalPassingMarks"
                    rules={[{ required: true, type: 'number', min: 0 }]}
                    style={{ flex: 1 }}
                  >
                    <InputNumber style={{ width: '100%' }} min={0} max={1000} step={1} />
                  </Form.Item>
                </Space.Compact>
                <Form.Item
                  label="Must pass both theory and practical"
                  name="mustPassBoth"
                  valuePropName="checked"
                  tooltip="If on, failing either component fails the subject."
                >
                  <Switch />
                </Form.Item>
              </>
            )}
          </Form>
        )}
      </Drawer>

      {classSectionId != null && (
        <BulkAssignDrawer
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          classSectionId={classSectionId}
          assignedSubjectIds={usedSubjectIds}
          allSubjects={subjects ?? []}
        />
      )}
      {classSectionId != null && (
        <CopyFromSectionModal
          open={copyOpen}
          onClose={() => setCopyOpen(false)}
          targetClassSectionId={classSectionId}
          allSections={sections ?? []}
        />
      )}
    </div>
  );
}

/* ---------- Bulk assign drawer ---------- */

interface BulkFormShape {
  subjectIds: number[];
  compulsory: boolean;
  medium: Medium;
  hasPractical: boolean;
  theoryTotalMarks: number;
  theoryPassingMarks: number;
  practicalTotalMarks?: number;
  practicalPassingMarks?: number;
  mustPassBoth: boolean;
}

function BulkAssignDrawer({
  open,
  onClose,
  classSectionId,
  assignedSubjectIds,
  allSubjects,
}: {
  open: boolean;
  onClose: () => void;
  classSectionId: number;
  assignedSubjectIds: Set<number>;
  allSubjects: { id: number; name: string }[];
}) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm<BulkFormShape>();
  const watchPractical = Form.useWatch('hasPractical', form);

  const available = useMemo(
    () => allSubjects.filter((s) => !assignedSubjectIds.has(s.id)),
    [allSubjects, assignedSubjectIds],
  );

  const bulk = useMutation({
    mutationFn: (subjects: ClassSubjectInput[]) =>
      academicsApi.bulkCreateClassSubjects(classSectionId, subjects),
    onSuccess: (res) => {
      const skippedHint = res.skipped > 0 ? ` · ${res.skipped} already assigned` : '';
      message.success(`Assigned ${res.created} subject${res.created === 1 ? '' : 's'}${skippedHint}`);
      qc.invalidateQueries({ queryKey: ['academics', 'class-subjects', classSectionId] });
      onClose();
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const onFinish = (v: BulkFormShape) => {
    if (!v.subjectIds || v.subjectIds.length === 0) {
      message.warning('Pick at least one subject.');
      return;
    }
    const subjects: ClassSubjectInput[] = v.subjectIds.map((id) => ({
      subjectId: id,
      compulsory: v.compulsory,
      medium: v.medium,
      hasPractical: v.hasPractical,
      theoryTotalMarks: v.theoryTotalMarks,
      theoryPassingMarks: v.theoryPassingMarks,
      practicalTotalMarks: v.hasPractical ? v.practicalTotalMarks : undefined,
      practicalPassingMarks: v.hasPractical ? v.practicalPassingMarks : undefined,
      mustPassBoth: v.hasPractical && v.mustPassBoth,
    }));
    bulk.mutate(subjects);
  };

  return (
    <Drawer
      title="Bulk assign subjects"
      open={open}
      onClose={onClose}
      size={560}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={bulk.isPending} onClick={() => form.submit()}>
            Assign
          </Button>
        </Space>
      }
    >
      {open && (
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            compulsory: true,
            medium: 'ENGLISH',
            hasPractical: false,
            theoryTotalMarks: 100,
            theoryPassingMarks: 33,
            mustPassBoth: false,
          }}
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="The settings below are applied to every selected subject. Edit individual subjects afterwards if any need different marks or medium."
          />
          <Form.Item
            label="Subjects"
            name="subjectIds"
            rules={[{ required: true, message: 'Pick at least one subject' }]}
            extra={
              available.length > 0 ? (
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0 }}
                  onClick={() => form.setFieldValue('subjectIds', available.map((s) => s.id))}
                >
                  Select all {available.length}
                </Button>
              ) : null
            }
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={available.length === 0 ? 'All subjects already assigned' : 'Pick subjects'}
              disabled={available.length === 0}
              options={available.map((s) => ({ value: s.id, label: s.name }))}
              maxTagCount="responsive"
            />
          </Form.Item>
          <Space.Compact style={{ width: '100%' }}>
            <Form.Item label="Compulsory" name="compulsory" valuePropName="checked" style={{ flex: 1 }}>
              <Switch />
            </Form.Item>
            <Form.Item label="Medium" name="medium" rules={[{ required: true }]} style={{ flex: 1 }}>
              <Select
                options={[
                  { value: 'ENGLISH', label: 'English' },
                  { value: 'URDU', label: 'Urdu' },
                ]}
              />
            </Form.Item>
          </Space.Compact>
          <Space.Compact style={{ width: '100%' }}>
            <Form.Item
              label="Theory total"
              name="theoryTotalMarks"
              rules={[{ required: true, type: 'number', min: 1 }]}
              style={{ flex: 1 }}
            >
              <InputNumber style={{ width: '100%' }} min={1} max={1000} step={5} />
            </Form.Item>
            <Form.Item
              label="Theory passing"
              name="theoryPassingMarks"
              rules={[{ required: true, type: 'number', min: 0 }]}
              style={{ flex: 1 }}
            >
              <InputNumber style={{ width: '100%' }} min={0} max={1000} step={1} />
            </Form.Item>
          </Space.Compact>
          <Form.Item label="Has practical" name="hasPractical" valuePropName="checked">
            <Switch />
          </Form.Item>
          {watchPractical && (
            <>
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item
                  label="Practical total"
                  name="practicalTotalMarks"
                  rules={[{ required: true, type: 'number', min: 1 }]}
                  style={{ flex: 1 }}
                >
                  <InputNumber style={{ width: '100%' }} min={1} max={1000} step={5} />
                </Form.Item>
                <Form.Item
                  label="Practical passing"
                  name="practicalPassingMarks"
                  rules={[{ required: true, type: 'number', min: 0 }]}
                  style={{ flex: 1 }}
                >
                  <InputNumber style={{ width: '100%' }} min={0} max={1000} step={1} />
                </Form.Item>
              </Space.Compact>
              <Form.Item
                label="Must pass both theory and practical"
                name="mustPassBoth"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </>
          )}
        </Form>
      )}
    </Drawer>
  );
}

/* ---------- Copy from another section modal ---------- */

interface SectionLite {
  id: number;
  className: string;
  sectionName: string;
  academicYearName: string;
  classSortOrder: number;
}

function CopyFromSectionModal({
  open,
  onClose,
  targetClassSectionId,
  allSections,
}: {
  open: boolean;
  onClose: () => void;
  targetClassSectionId: number;
  allSections: SectionLite[];
}) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [sourceId, setSourceId] = useState<number | undefined>(undefined);

  const sourceOptions = useMemo(
    () =>
      allSections
        .filter((s) => s.id !== targetClassSectionId)
        .slice()
        .sort(
          (a, b) =>
            a.classSortOrder - b.classSortOrder || a.sectionName.localeCompare(b.sectionName),
        )
        .map((s) => ({
          value: s.id,
          label: `${s.className} · ${s.sectionName} · ${s.academicYearName}`,
        })),
    [allSections, targetClassSectionId],
  );

  const preview = useQuery({
    queryKey: ['academics', 'class-subjects', sourceId, 'preview'],
    queryFn: () => academicsApi.listClassSubjects(sourceId!),
    enabled: sourceId != null && open,
  });

  const copy = useMutation({
    mutationFn: () =>
      academicsApi.copyClassSubjects(targetClassSectionId, sourceId!),
    onSuccess: (res) => {
      const skippedHint = res.skipped > 0 ? ` · ${res.skipped} skipped (already on target)` : '';
      message.success(`Copied ${res.created} subject${res.created === 1 ? '' : 's'}${skippedHint}`);
      qc.invalidateQueries({ queryKey: ['academics', 'class-subjects', targetClassSectionId] });
      onClose();
      setSourceId(undefined);
    },
    onError: (e) => message.error(extractError(e)),
  });

  return (
    <Modal
      title="Copy subjects from another section"
      open={open}
      onCancel={() => {
        onClose();
        setSourceId(undefined);
      }}
      onOk={() => copy.mutate()}
      okButtonProps={{ disabled: sourceId == null || (preview.data && preview.data.length === 0) }}
      confirmLoading={copy.isPending}
      okText={
        preview.data
          ? `Copy ${preview.data.length} subject${preview.data.length === 1 ? '' : 's'}`
          : 'Copy'
      }
      destroyOnHidden
    >
      <Space orientation="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Typography.Text strong>Source section</Typography.Text>
          <Select
            style={{ width: '100%', marginTop: 6 }}
            showSearch
            optionFilterProp="label"
            placeholder="Pick a section to copy from"
            value={sourceId}
            onChange={(v) => setSourceId(v)}
            options={sourceOptions}
          />
        </div>

        {sourceId != null && preview.isFetching && (
          <Typography.Text type="secondary">Loading subjects…</Typography.Text>
        )}

        {sourceId != null && preview.data && (
          preview.data.length === 0 ? (
            <Alert type="warning" showIcon message="Source section has no subjects assigned." />
          ) : (
            <Alert
              type="info"
              showIcon
              message={`${preview.data.length} subject${preview.data.length === 1 ? '' : 's'} will be copied with the same marks & medium config.`}
              description={
                <Space size={[4, 4]} wrap style={{ marginTop: 6 }}>
                  {preview.data.map((cs) => (
                    <Tag key={cs.id} variant="filled">
                      {cs.subjectName}
                    </Tag>
                  ))}
                </Space>
              }
            />
          )
        )}

        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Subjects already present on the target section are skipped automatically.
        </Typography.Text>
      </Space>
    </Modal>
  );
}

